import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass, User as UserIcon, Menu, Sparkles, MapPin } from "lucide-react";
import { useAuth } from "../App";
import FabMenuModal from "./FabMenuModal";

const TAB_DEFS = [
  { id: "explorar", labelKey: "nav.explore", to: "/search", icon: Compass, match: (p, s) => p.startsWith("/search") && !s.includes("view=map") },
  { id: "guia",     labelKey: "nav.guide",   to: "/guia",   icon: Sparkles, match: (p) => p.startsWith("/guia") },
  { id: "mapa",     labelKey: "nav.map",     to: "/search?view=map", icon: MapPin, match: (p, s) => p.startsWith("/search") && s.includes("view=map") },
  { id: "perfil",   labelKey: "nav.profile", to: "/profile", icon: UserIcon, match: (p) => p.startsWith("/profile") },
];

export default function BottomNav() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const TABS = TAB_DEFS.map((d) => ({ ...d, label: t(d.labelKey) }));

  const goTab = (to) => {
    if (to.startsWith("/profile") && !isAuthenticated) {
      navigate("/login");
      return;
    }
    // For Mapa/Explorar on /search: preserve existing query params (like city) and toggle ?view=map
    if (to === "/search?view=map" && location.pathname === "/search") {
      const sp = new URLSearchParams(location.search);
      sp.set("view", "map");
      navigate(`/search?${sp.toString()}`);
      return;
    }
    if (to === "/search" && location.pathname === "/search") {
      const sp = new URLSearchParams(location.search);
      sp.delete("view");
      const qs = sp.toString();
      navigate(qs ? `/search?${qs}` : "/search");
      return;
    }
    navigate(to);
  };

  return (
    <>
      {/* Spacer so page content is never hidden under the bar on mobile */}
      <div className="lg:hidden h-20" aria-hidden="true" />

      <nav
        data-testid="bottom-nav"
        className="lg:hidden fixed bottom-0 inset-x-0 z-[70] bg-black/95 backdrop-blur-xl border-t border-white/10 pb-[env(safe-area-inset-bottom)]"
      >
        <div className="relative flex items-end justify-between px-2 pt-2 pb-2 gap-1">
          {/* Left two tabs */}
          {TABS.slice(0, 2).map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              active={tab.match(location.pathname, location.search)}
              onClick={() => goTab(tab.to)}
            />
          ))}

          {/* Center FAB (hamburger) — opens FabMenuModal */}
          <button
            type="button"
            data-testid="bottom-nav-fab"
            aria-label={t("nav.menu")}
            onClick={() => setMenuOpen(true)}
            className="relative -mt-8 flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 via-blue-500 to-blue-700 text-white shadow-[0_8px_28px_rgba(34,211,238,0.45)] ring-4 ring-black/95 active:scale-95 transition-transform shrink-0"
          >
            <span className="absolute inset-0 rounded-full bg-cyan-400/25 blur-xl -z-10" aria-hidden />
            <Menu className="w-7 h-7" strokeWidth={2.5} />
          </button>

          {/* Right two tabs */}
          {TABS.slice(2).map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              active={tab.match(location.pathname, location.search)}
              onClick={() => goTab(tab.to)}
            />
          ))}
        </div>
      </nav>

      <FabMenuModal open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}

function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      data-testid={`bottom-nav-tab-${tab.id}`}
      onClick={onClick}
      className="flex flex-col items-center justify-end gap-1 flex-1 min-w-0 max-w-[64px] py-1.5 active:scale-95 transition-transform"
      aria-current={active ? "page" : undefined}
    >
      <span className="relative">
        <Icon
          className={`w-6 h-6 transition-colors ${active ? "text-cyan-400" : "text-white/55"}`}
          strokeWidth={active ? 2.5 : 2}
        />
      </span>
      <span className={`text-[11px] font-medium tracking-wide ${active ? "text-cyan-400" : "text-white/55"}`}>
        {tab.label}
      </span>
      <span
        className={`block h-[3px] w-6 rounded-full transition-all ${active ? "bg-cyan-400" : "bg-transparent"}`}
        aria-hidden
      />
    </button>
  );
}
